<template lang="pug">
div
  AppScrollableTable(
    :has-fixed-column="false"
    @table-scroll="hidePopover"
  )
    AppTable(
      ref="table"
      hoverable
      :loading="showTableLoading"
      selectable
      cross-page-selectable
      :table-data="displayRows"
      :value="batchActionIds"
      @selected="selectedMembers"
      @sort="setSortLoadTable"
      @header-component-change="handleHeaderComponentChange"
    )
      template(#empty)
        #no-data.d-flex.flex-column.align-items-center.mt-20
          img(
            :src="IMAGE_NO_DATA_PLACEHOLDER"
            width="250"
          )
          p.mb-0.max-w-full.text-center
            | No members here. Try a new search
      AppTableColumn(
        v-slot="{ row }"
        header="Member"
        name="name"
        :sort-by="sortBy"
      )
        .member-name-container
          .user-status.user-status--speck(
            :class="{ 'is-online': row.is_online }"
            @click="showMemberPopover"
          )
            PopoverAvatar(
              v-if="row.status === 0"
              :user-id="row.user.id"
              :organization-id="organization.id"
              avatar-style="medium"
              :hide-member-popover="hideMemberPopover"
            )
            HubAvatar.avatar-medium(
              v-else
              :user-id="row.user.id"
            )
          .ml-5(v-if="isDisabled(row)")
            | {{ row.user.name }}
            template(v-if="hasCorporateApp && row.member_type")
              br
              .member-type.text-grey {{ row.member_type }}
          a.member-name.ml-5(
            v-else
            v-tooltip="{ html: true, container: 'self', closeOnClick: true }"
            data-original-title="Go to member profile"
            data-testid="member-name-link"
            :data-user-id="row.user.id"
            :href="getMemberUrl(row.user.id)"
          )
            | {{ row.user.name }}
            template(v-if="hasCorporateApp && row.member_type")
              br
              .member-type.text-grey {{ row.member_type }}
          .deactivated(
            v-if="row.user.deleted"
            v-tooltip="{ placement: 'bottom' }"
            title="This user has deactivated their account. They can reactivate it by attempting to login."
          )
            | (Deactivated)
      AppTableColumn(
        v-slot="{ row }"
        header="Info"
        :visible="tableColumns.show_member_info"
        name="info"
        header-dropdown
        header-dropdown-extra-classes="app-toggle-dropdown-header"
        :header-dropdown-fields="infoFields"
        header-dropdown-component="MembersInfoFields"
      )
        PopoverProfileData(
          v-tooltip="{ closeOnClick: true }"
          :organization-id="organization.id"
          :hide-member-popover="hideMemberPopover"
          :membership="row"
          :profile-fields="infoFields"
          :any-fields-hidden="anyInfoFieldsHidden"
          column-type="info"
          title="Click to view details"
          data-tipclass="member-popover-tooltip"
        )
          MemberProfileData(
            :profile-fields="infoFields"
            :member-data="row"
          )
      AppTableColumn(
        v-slot="{ row }"
        header="Employment"
        :visible="tableColumns.show_member_employment"
        name="employment"
        header-dropdown
        header-dropdown-extra-classes="app-toggle-dropdown-header"
        :header-dropdown-fields="employmentFields"
        header-dropdown-component="MembersEmploymentFields"
      )
        PopoverProfileData(
          v-tooltip="{ closeOnClick: true }"
          :organization-id="organization.id"
          :hide-member-popover="hideMemberPopover"
          :membership="row"
          :profile-fields="employmentFields"
          :any-fields-hidden="anyEmploymentFieldsHidden"
          column-type="employment"
          title="Click to view details"
          data-tipclass="member-popover-tooltip"
        )
          MemberProfileData(
            column-type="employment"
            :profile-fields="employmentFields"
            :member-data="row"
          )
      AppTableColumn(
        v-if="hasCorporateApp"
        v-slot="{ row }"
        header="Work email"
        :visible="tableColumns.show_email"
        name="work_email"
        :sort-by="sortBy"
      )
        template(v-if="!isDisabled(row) && row.from_account_provisioning && !row.user.scim_managed")
          WorkEmailPopover(
            :key="row.id"
            :member="row"
            :organization-id="organization.id"
          )
            template(#trigger)
              AppHoverLink(:title="editEmailTooltip")
                | {{ row.user.email }}

        template(v-else)
          span(
            v-tooltip="row.user.scim_managed"
            data-original-title="Member managed by SCIM"
          )
            | {{ row.user.email }}

      AppTableColumn(
        v-slot="{ row }"
        header="Status"
        :visible="tableColumns.show_status"
        name="status"
        :sort-by="sortBy"
      )
        span(v-if="row.status == 0")
          | Active
        span(v-else)
          | Removed
      AppTableColumn(
        v-slot="{ row }"
        header="Role"
        :visible="tableColumns.show_role"
      )
        AppHoverLink(
          :title="editRoleTooltip"
          :enabled="!isDisabled(row)"
          :href="getMemberUrl(row.user.id, 'projects-and-roles')"
        )
          | {{ row.role }}
      AppTableColumn(
        v-if="showTeamsUI"
        v-slot="{ row }"
        :visible="tableColumns.show_teams"
        header="Teams"
        name="teams"
        :sort-by="sortBy"
      )
        AppHoverLink(
          title="Edit this member's teams"
          :enabled="!isDisabled(row)"
          :href="getMemberUrl(row.user.id, teamsTab)"
        )
          template(v-if="!row.teams_count.all")
            | None
          template(v-else)
            AppAvatarInitial.avatar-overlaps(
              size="medium"
              :text="row.teams_count.first_team_name"
            )
            AppAvatarInitial.avatar-overlaps(
              v-if="row.teams_count && row.teams_count.all === 2"
              size="medium"
              :text="row.teams_count.last_team_name"
            )
            span.more-members-badge.light(v-if="row.teams_count.all > 2")
              | +{{ row.teams_count.all - 1 }}
      AppTableColumn(
        v-if="hasProjects"
        v-slot="{ row }"
        :visible="tableColumns.show_projects"
        header="Projects"
        name="projects"
        :sort-by="sortBy"
      )
        AppHoverLink(
          title="Edit this member's role and project memberships"
          :enabled="!isDisabled(row)"
          :href="getMemberUrl(row.user.id, 'projects-and-roles')"
        )
          template(v-if="row.projects_count && row.projects_count.all_active > 0")
            | {{ row.projects_count.all_active }}
          template(v-else)
            | None
      AppTableColumn(
        v-if="hasWorkOrders"
        v-slot="{ row }"
        :visible="tableColumns.show_work_orders"
        header="Work orders"
        name="work_orders"
        :sort-by="sortBy"
      )
        template(v-if="row.work_orders_count && row.work_orders_count.all_active > 0")
          | {{ row.work_orders_count.all_active }}
        template(v-else)
          | None
      AppTableColumn(
        v-slot="{ row }"
        :visible="tableColumns.show_payment"
        header="Payment"
        name="bill_rate"
        :sort-by="sortBy"
      )
        .rate-container
          div
            AppHoverLink.rates(
              v-if="canViewPayRates(row)"
              :enabled="!isDisabled(row) && canViewBillRates"
              title="Edit this member's pay rate"
              :href="getMemberUrl(row.user.id, 'pay / bill')"
            )
              small.text-grey Pay rate:
              template(v-if="!row.pay_rate && !row.fixed_amount")
                small.text-lighter
                  |
                  | No pay rate
              template(v-else-if="row.pay_rate")
                small.pay-rate
                  | {{ ' ' + $filters.currency(row.pay_rate, organization.currency) }}
              template(v-else-if="row.fixed_amount !== null")
                small.fixed-amount
                  | {{ ' ' + $filters.currency(Number(row.fixed_amount), organization.currency) }}
          div
            AppHoverLink.bill-rate-container(
              v-if="canViewBillRates"
              :enabled="!isDisabled(row)"
              title="Edit this member's bill rate"
              :href="getMemberUrl(row.user.id, 'pay / bill')"
            )
              small.text-grey Bill rate:
              small.bill-rate(v-if="row.bill_rate !== null")
                |
                | {{ $filters.currency(row.bill_rate, organization.currency) }}
              small.text-lighter(v-else)
                |
                | No bill rate
          AppHoverLink.pay-type-container(
            v-if="showPayType(row)"
            :enabled="!isDisabled(row) && canViewBillRates"
            title="Edit this member's pay period"
            :href="getMemberUrl(row.user.id, 'pay / bill')"
          )
            template(v-if="row.pay_rate")
              span.text-lighter
                small.text-lighter Hourly
              span.divider(v-if="payPeriodText(row.pay_period)")
            template(v-else-if="row.fixed_amount !== null")
              span.type.text-lighter
                small.text-lighter Fixed
              span.divider(v-if="payPeriodText(row.pay_period)")
            small.pay-period.text-lighter
              | {{ payPeriodText(row.pay_period) }}
            span.timesheets(v-if="timesheetApprovals && row.timesheets_enabled && canViewBillRates")
              span.divider
              small.text-lighter
                | Timesheet approvals
      AppTableColumn(
        v-slot="{ row }"
        :visible="tableColumns.show_limits"
        header="Limits"
        name="limits"
        :sort-by="sortBy"
      )
        AppHoverLink(
          title="Edit this member's limits"
          :enabled="!isDisabled(row)"
          :href="getMemberUrl(row.user.id, 'work hours')"
        )
          .limits-container
            .text-with-icon.weekly-limit
              template(v-if="row.limits.weekly_limit !== null")
                template(v-if="weeklyLimitDiff(row.limits)")
                  | {{ formatLimitDuration(row.limits.current_weekly_limit) }}
                  small
                    | &nbsp;/ week&nbsp;
                  i.hi.hi-info(
                    v-tooltip="{ placement: 'top' }"
                    :title="`The weekly limit for the current week of ${pluralizeHours(row.limits.current_weekly_limit)} differs from the recurring weekly limit of ${pluralizeHours(row.limits.weekly_limit)}`"
                  )
                template(v-else)
                  | {{ formatLimitDuration(row.limits.weekly_limit) }}
                  small
                    | &nbsp;/ week
              template(v-else)
                small.text-lighter No weekly limit
            span(v-if="limitsEnabled")
              br
              .text-with-icon.daily-limit
                template(v-if="row.limits.limits_by_shifts")
                  small.text-lighter Limited by shifts
                template(v-else)
                  template(v-if="row.limits.daily_limit !== null")
                    template(v-if="dailyLimitDiff(row.limits)")
                      | {{ formatLimitDuration(row.limits.current_daily_limit) }}
                      i.hi.hi-info.a-tooltip.text-light(
                        v-tooltip="{ placement: 'bottom' }"
                        :title="`The daily limit for today of ${pluralizeHours(Number(row.limits.current_daily_limit))} differs from the recurring daily limit of ${pluralizeHours(row.limits.daily_limit)}`"
                      )
                    template(v-else)
                      | {{ formatLimitDuration(row.limits.daily_limit) }}
                    small
                      | &nbsp;/ day&nbsp
                  template(v-else)
                    small.text-lighter No daily limit
      AppTableColumn(
        v-slot="{ row }"
        header="Screenshots"
        header-tooltip="Screenshots are measured per calendar month"
        header-tooltip-type="info"
        name="screenshots_count"
        :visible="tableColumns.show_screenshots"
      )
        .metric-value(style="display: flex; flex-direction: column; align-items: center; text-align: center;")
          | {{ row.screenshots_count }}
          a.view-link(
            :href="row.screenshots_url"
            target="_blank"
            style="color: #55b9ff; font-size: 12px; font-weight: normal; cursor: pointer; margin-top: 2px;"
          ) View

      AppTableColumn(
        v-slot="{ row }"
        header="Apps & URLs"
        header-tooltip="Apps/URLs are measured per calendar month"
        header-tooltip-type="info"
        name="apps_urls_count"
        :visible="tableColumns.show_apps_urls"
      )
        .metric-value(style="display: flex; flex-direction: column; align-items: center; text-align: center;")
          | {{ row.apps_count + row.urls_count }}
      AppTableColumn(
        v-slot="{ row }"
        :visible="tableColumns.show_time_tracking"
        header="Time tracking status"
      )
        span.label.label-success(v-if="!isProjectViewer(row.role) && row.trackable")
          | Enabled
        span.label.label-danger(v-else)
          | Disabled
      AppTableColumn(
        v-slot="{ row }"
        :visible="tableColumns.show_date_added"
        header="Date added"
        name="added"
        :sort-by="sortBy"
      )
        | {{ row.created_at }}
      AppTableColumn(
        v-slot="{ row }"
        :visible="tableColumns.show_date_removed"
        header="Date removed"
        name="removed"
        :sort-by="sortBy"
      )
        | {{ row.removed_at }}
      AppTableColumn(
        v-slot="{ row }"
        header=""
      )
        AppDropdown.pull-right.table-actions-dropdown(
          v-if="row.status == 0 && !row.disabled"
          direction="right"
          label="Actions"
          size="small"
          track
          track-scope="Members table"
          :data-testid="`member-actions-${row.user.id}`"
        )
          AppDropdownItem(
            v-track:click="editInfoTrack"
            :track="false"
            :href="getMemberUrl(row.user.id)"
          )
            | Edit info
          template(v-if="showMergeDuplicateAction")
            AppDropdownItem(
              v-if="row.member_type === 'Silent'"
              :data-test-id="`member-action-merge-${row.user.id}`"
              @click="openMergeDuplicateDialog(row)"
            )
              | Merge duplicate member
            AppDropdownItem(
              v-else
              v-tooltip="{ placement: 'left' }"
              :data-test-id="`member-action-merge-${row.user.id}`"
              disabled
              title="Only Silent app members can be merged"
              @click.stop
            ) Merge duplicate member
          AppDropdownItem(
            v-track:click="editRoleTrack"
            :track="false"
            :href="getMemberUrl(row.user.id, 'projects-and-roles')"
          )
            | {{ editRoleActionText }}
          AppDropdownItem(
            v-if="canViewBillRates"
            :href="getMemberUrl(row.user.id, 'pay / bill')"
          )
            | Edit payment details
          AppDropdownItem(
            v-track:click="editLimitsTrack"
            :track="false"
            :href="getMemberUrl(row.user.id, 'work hours')"
          )
            | Edit limits
          template(v-if="!isProjectViewer(row.role)")
            AppDropdownItem(
              v-if="row.trackable"
              v-track:click="disableTrackingTrack"
              :track="false"
              @click="onDisableTracking(row)"
            )
              | Disable tracking
            AppDropdownItem(
              v-if="!row.trackable"
              v-tooltip="row.view_only"
              :disabled="row.view_only"
              data-original-title="Cannot enable time tracking for a view-only member"
              @click="onEnableTracking(row.id, row.user.name)"
            )
              | Enable tracking
          template(v-else)
            AppDropdownItem(
              disabled
              @click.stop
            )
              | Enable tracking
          AppDropdownItem(
            v-if="currentUserIsManagement"
            v-tooltip="row.from_account_provisioning && !row.can_reset_provisioned_password"
            :track="false"
            :disabled="row.from_account_provisioning && !row.can_reset_provisioned_password"
            data-original-title="You cannot reset password for this member"
            @click="openResetDialog(row)"
          ) Reset password
          AppDropdownItem(
            v-if="canRemoveMember(row.role)"
            v-tooltip="row.user.scim_managed"
            :track="false"
            :disabled="row.user.scim_managed"
            data-placement="left"
            data-original-title="Member managed by SCIM"
            @click="confirmRemoveMember(row.id)"
          )
            | Remove member
  AppPagination(
    v-if="showPagination"
    :pagination="pagination"
    :resource="{ one: 'member', many: 'members' }"
    @page-changed="onPageChanged"
  )

  ClientAgentManualMergeDialog(
    v-if="showMergeDialog"
    :duplicate-user="duplicateUser"
    @close="closeMergeDialog"
    @confirm-merge="confirmMergeDuplicate"
  )
</template>

<script>
import { isNil } from 'lodash-es';
import { mapActions, mapGetters, mapState } from 'vuex';
import { IMAGE_NO_DATA_PLACEHOLDER } from '~/rails_assets';
import { EXTERNAL_PAY_PERIOD, MEMBERSHIP_PAY_PERIOD_OPTIONS, PROJECT_VIEWER_ROLE } from '../../app_constants';
import { formatDuration } from '~/utils/time_utils';
import PopoverAvatar from '~/components/PopoverAvatar.vue';
import PopoverProfileData from '~/components/members/PopoverProfileData.vue';
import MemberProfileData from '~/components/members/MemberProfileData.vue';
import WorkEmailPopover from '~/components/members/WorkEmailPopover.vue';
import ClientAgentManualMergeDialog from '~/components/client_agents/ClientAgentManualMergeDialog.vue';
import { OrganizationsResource } from '~/resources';

export default {
  name: 'MembersTable',
  components: {
    PopoverAvatar,
    MemberProfileData,
    PopoverProfileData,
    WorkEmailPopover,
    ClientAgentManualMergeDialog,
  },
  props: {
    hasProjects: {
      type: Boolean,
      default: false,
    },
    hasWorkOrders: {
      type: Boolean,
      default: false,
    },
    showTeamsUI: {
      type: Boolean,
      default: false,
    },
    mergingUserIds: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['page-changed', 'about-change', 'reset-password', 'merge-started'],
  data() {
    const label = 'Action buttons used';

    return {
      IMAGE_NO_DATA_PLACEHOLDER,
      MEMBERSHIP_PAY_PERIOD_OPTIONS,
      hideMemberPopover: false,
      showMembersAboutPicker: false,
      showMergeDialog: false,
      duplicateUser: null,
      tooltipOptions: Object.freeze({ placement: 'left', html: true }),
      passwordResetTrack: Object.freeze({ label: 'Member password reset', action: 'password_reset' }),
      editInfoTrack: Object.freeze({ label, action: 'edit_info' }),
      editRoleTrack: Object.freeze({ label, action: 'edit_role_memberships' }),
      editLimitsTrack: Object.freeze({ label, action: 'edit_limits' }),
      disableTrackingTrack: Object.freeze({ label, action: 'disable_tracking' }),
      removeMemberTrack: Object.freeze({ label, action: 'remove_member' }),
      merging: false,
    };
  },
  computed: {
    ...mapState({
      organization: 'organization',
      pagination: 'pagination',
      userOrganizations: 'user_organizations',
      userOrganizationsQuery: 'user_organizations_query',
      currentUser: 'current_user',
      removeMemberUrl: 'remove_member_url',
      showMemberUrl: 'show_member_url',
      batchActionIds: 'batch_action_ids',
    }),
    ...mapGetters([
      'hasFeature',
      'showTableLoading',
      'teamsFeature',
      'timesheetApprovals',
      'limitsEnabled',
      'tableColumns',
      'aboutFields',
      'infoFields',
      'employmentFields',
      'anyInfoFieldsHidden',
      'anyEmploymentFieldsHidden',
      'currentUserIsManagement',
    ]),
    displayRows() {
      if (this.showMergeDuplicateAction) {
        return this.userOrganizations.map((row) => {
          const disabledByServer = row.dup_merge_in_progress || row.main_merge_in_progress;
          const disabledByClient = this.mergingUserIds.includes(row.user.id);
          const merging = disabledByServer || disabledByClient;
          const trClasses = merging ? [...(row.trClasses || []), 'row-merging'] : row.trClasses;

          return merging ? { ...row, trClasses } : row;
        });
      }

      return this.userOrganizations;
    },
    sortBy() {
      const { sort_by: field, sort_order: direction } = this.userOrganizationsQuery;

      return { field, direction };
    },
    canViewBillRates() {
      return this.organization.active && this.currentUser.can_manage_billing;
    },
    hasData() {
      return this.userOrganizations && this.userOrganizations.length > 0;
    },
    showPagination() {
      return this.pagination && this.hasData;
    },
    editRoleTooltip() {
      if (this.hasProjects) {
        return "Edit this member's role and project memberships";
      }

      return "Edit this member's role";
    },
    editEmailTooltip() {
      return "Edit this member's email";
    },
    editRoleActionText() {
      if (this.hasProjects) {
        return 'Edit role and memberships';
      }

      return 'Edit role';
    },
    teamsTab() {
      return 'projects-and-roles';
    },
    hasCorporateApp() {
      return this.hasFeature('corporate');
    },
    showMergeDuplicateAction() {
      return this.hasCorporateApp;
    },
  },
  methods: {
    ...mapActions([
      'disableTracking',
      'enableTracking',
      'setBatchActionIds',
      'setFilterLoadTable',
      'updatePaymentSection',
    ]),
    setSortLoadTable({ direction, field }) {
      this.setFilterLoadTable({ sort_by: field, sort_order: direction });
    },
    getMemberUrl(userId, tab, section = null) {
      // FIXME: for some reason it's user_id in the url
      return (
        [
          this.showMemberUrl.replace('__USER_ORGANIZATION_ID__', userId),
          tab && `?tab=${tab}`,
          section && `&payments_section=${section}`,
        ].filter(Boolean).join('') || undefined
      );
    },
    canViewPayRates(member) {
      return this.currentUser.can_manage_billing || this.currentUser.id === member.user.id;
    },
    canRemoveMember(memberRole) {
      return this.currentUser.can_manage_billing || memberRole !== 'Organization owner';
    },
    confirmRemoveMember(memberId) {
      this.trackAction(this.removeMemberTrack);

      const url = this.removeMemberUrl.replace('__USER_ORGANIZATION_ID__', memberId);

      AjaxDialog.run(url);
    },
    dailyLimitDiff(limits) {
      if (limits.current_daily_limit) {
        return Number(limits.current_daily_limit) !== limits.daily_limit;
      }
    },
    weeklyLimitDiff(limits) {
      return Number(limits.current_weekly_limit) !== limits.weekly_limit;
    },
    formatLimitDuration(limit) {
      return formatDuration(limit * 3600, { showSeconds: 'never' });
    },
    isProjectViewer(role) {
      return role === PROJECT_VIEWER_ROLE;
    },
    onPageChanged(page) {
      this.$emit('page-changed', page);
    },
    payPeriodText(val) {
      if (!isNil(val)) {
        const payPeriod = MEMBERSHIP_PAY_PERIOD_OPTIONS.find((period) => period.id === val.toString());

        return payPeriod ? payPeriod.text : EXTERNAL_PAY_PERIOD;
      }
    },
    pluralizeHours(limit) {
      return `${limit} ${this.$filters.pluralize(limit, 'hour')}`;
    },
    selectedMembers(selected) {
      this.setBatchActionIds(selected);
    },
    isDisabled(row) {
      return row.status === 1;
    },
    onEnableTracking(id, name) {
      const success = (response) => {
        if (response?.success) {
          this.$appMessage(`Enabled tracking for ${name}`);
        } else {
          this.$appMessage(`Unable to enable tracking for ${name}`);
        }
      };

      this.enableTracking(id).then(success, this.showUnexpectedError);
    },
    async onDisableTracking(row) {
      const success = (response) => {
        if (response?.success) {
          this.$appMessage(`Disabled tracking for ${row.user.name}`);
        } else {
          this.$appMessage(`Unable to disable tracking for ${row.user.name}`);
        }
      };

      // When attempting to disable time tracking for our own account, we need to stop the timer as well.
      // This must be done BEFORE disabling - we wouldn't have permission to do so afterwards (nor any way
      // to manually do so in the UI).
      if (this.currentUser.id === row.user.id) {
        let changedState = false;

        if (window.WebTimer.backend.state.tracking) {
          changedState = true;
          await this.stopMyTimer(row.user.id);
        }

        // Right now, the only way we can be sure if the timer is stopped is by evaluating if tracking = false,
        // be it because we stopped it just now, or because it was already stopped before triggering this action.
        if (!window.WebTimer.backend.state.tracking) {
          this.disableTracking(row.id).then(success, this.showUnexpectedError);
          // Check if we changed user tracking state, if not return so that we do not show timer messages
          if(changedState) {
            this.$appMessage('Stopped timer');
          }
        } else {
          this.$appMessage('Unable to stop timer');
        }

        return;
      }

      this.disableTracking(row.id).then(success, this.showUnexpectedError);
    },
    showUnexpectedError() {
      this.$appMessage('Unexpected error. Please try again.');
    },
    async stopMyTimer() {
      // FIXME: we shouldn't be doing this!
      // Vue components should communicate via parent/child events, central buses or vuex.
      // Unfortunately, because the web timer isn't built so that we can properly communicate
      // with it, we have to access it from the global scope (window).
      try {
        await window.WebTimer.backend.stopTimer();
      } catch (e) {
        this.showUnexpectedError();
      }
    },
    hidePopover() {
      this.hideMemberPopover = true;
    },
    showMemberPopover() {
      this.hideMemberPopover = false;
    },
    handleHeaderComponentChange(payload) {
      this.$emit('about-change', payload);
    },
    showPayType(row) {
      return (
        row.pay_rate ||
        row.fixed_amount !== null ||
        this.payPeriodText(row.pay_period) ||
        (this.timesheetApprovals && row.timesheets_enabled)
      );
    },
    // Tracked from the click handlers instead of `v-track:click`, which also fires for disabled items.
    trackAction({ label, ...props }) {
      window.analytics?.track(label, props);
    },
    openResetDialog(row) {
      this.trackAction(this.passwordResetTrack);
      this.$emit('reset-password', row);
    },
    openMergeDuplicateDialog(row) {
      this.duplicateUser = row;
      this.showMergeDialog = true;
    },
    closeMergeDialog() {
      this.showMergeDialog = false;
      this.duplicateUser = null;
    },
    async confirmMergeDuplicate({ fromIds, toId }) {
      if (this.merging) {
        return;
      }
      this.merging = true;
      try {
        const params = { from_user_ids: fromIds, to_user_id: toId };

        await OrganizationsResource.mergeMembers({ id: this.organization.id }, params);

        this.$emit('merge-started', { fromIds, toId });
      } catch (e) {
        this.$appMessage('Something went wrong when we tried to merge these members. Please try again later.', {
          header: 'Merge failed',
        });
      } finally {
        this.merging = false;
        this.closeMergeDialog();
      }
    },
  },
};
</script>
